import { useState } from 'react'

import type { LibraryFolder } from '../../api/library'
import { type Subject, type SubjectContent, isArchived } from '../../api/subjects'
import { Button } from '../../ui/Control'
import { FolderPicker } from './FolderPicker'
import { MarkingSchemes } from './MarkingSchemes'
import { SourceKeys } from './SourceKeys'
import { SubjectReview } from './SubjectReview'
import { RichText } from '../../ui/RichText'
import { useSaving } from '../../ui/useSaving'

/**
 * One subject, opened.
 *
 * Everything a subject is, on one surface: what it is called, what it is about, the folders anchored
 * to it, where new things at it are filed, and everything that belongs to it grouped by kind.
 *
 * Two things about it are decisions rather than layout.
 *
 * **An anchored folder is shown as what it is: a rule.** Everything in it and below it belongs to
 * this subject, no file has been touched, and taking the anchor away takes the belonging away again
 * without changing a single document. That sentence is on the screen, because a belonging somebody
 * cannot explain is a belonging they will not trust.
 *
 * **Content that is here through a folder is marked and not removable from here.** It is a statement
 * about that folder — the way to undo it is at the folder, or by moving the document out of it.
 *
 * On a phone this surface takes the whole frame and the list steps back; `Lanes` does that, and the
 * way back is the frame's own control. Nothing here names a device.
 */
export interface SubjectSheetProps {
  readonly subject: Subject
  readonly content: SubjectContent | null
  /** Every subject there is, for the merge control. */
  readonly all: readonly Subject[]
  /** The whole folder tree, for anchoring one. */
  readonly folders: readonly LibraryFolder[]
  readonly onChange: (name: string, description: string, descriptionMarkup: string) => void
  readonly onArchive: (archived: boolean) => void
  readonly onMerge: (from: string) => void
  readonly onRemove: () => void
  readonly onAnchor: (folderId: string) => void
  readonly onUnanchor: (folderId: string) => void
  readonly onRootFolder: (folderId: string | null) => void
  readonly onOpenContent: (kind: string, id: string) => void
  readonly onClose: () => void
}

export function SubjectSheet({
  subject,
  content,
  all,
  folders,
  onChange,
  onArchive,
  onMerge,
  onRemove,
  onAnchor,
  onUnanchor,
  onRootFolder,
  onOpenContent,
  onClose,
}: SubjectSheetProps) {
  /*
   * Seeded once and never synchronised afterwards: the list keys this surface by what the service
   * last said about the subject, so a change arriving is a new surface with the new values in it.
   */
  const [name, setName] = useState(subject.name)
  const [description, setDescription] = useState(subject.description)
  // Saved as it is written, like every other text in MyLib.
  const saving = useSaving((wanted) => {
    if (name.trim() !== '') {
      onChange(name, wanted, subject.descriptionMarkup)
    }
    return Promise.resolve()
  })
  const [merging, setMerging] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)

  // The picker shows the whole tree and marks these as already spoken for, rather than hiding them:
  // a tree with holes where somebody looks twice is a tree they stop trusting.
  const anchored = subject.folders.map((folder) => folder.id)

  return (
    <article
      className="sheet sheet--subject"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close this subject"
          onClick={onClose}
        />
        <h2 className="sheet__title-text">{subject.name}</h2>
        <p className="sheet__facts">
          <span>{isArchived(subject) ? 'Archived' : 'Running'}</span>
        </p>
      </header>

      <div className="subject-detail">
        <div className="subject-detail__field">
          <label className="subject-detail__label" htmlFor="subject-name">
            Name
          </label>
          <input
            id="subject-name"
            className="subject-detail__text"
            value={name}
            onChange={(event) => {
              setName(event.target.value)
            }}
            onBlur={() => {
              if (
                name.trim() !== '' &&
                (name !== subject.name || description !== subject.description)
              ) {
                onChange(name, description, subject.descriptionMarkup)
              }
            }}
          />
        </div>

        <div className="subject-detail__field">
          <label className="subject-detail__label" htmlFor="subject-about">
            What it is about
          </label>
          <RichText
            compact
            text={description}
            format={subject.descriptionMarkup}
            label="What this subject is about"
            purpose="subject-about"
            initially="text"
            placeholder="A sentence or two."
            onChange={(wanted) => {
              setDescription(wanted)
              saving.typed(wanted)
            }}
            onFormat={(descriptionMarkup) => {
              onChange(name, description, descriptionMarkup)
              return Promise.resolve()
            }}
            onSubmit={saving.now}
          />
        </div>

        <section className="subject-detail__part">
          <h3 className="subject-detail__part-title">Folders</h3>
          <p className="subject-detail__aside">
            Everything in an anchored folder — and in everything below it — belongs to this subject.
            No file is touched, and taking the anchor away takes the belonging away again.
          </p>
          {/*
            **Where new things are filed is said at the folder, not in a second list beside it.**

            There were two controls here: one to anchor a folder, and a `<select>` underneath
            repeating the anchored ones so that one of them could be named the filing target. Two
            lists of the same folders, and whoever anchored their first one still had to notice the
            second control and use it — otherwise everything the subject produced went "nowhere in
            particular", which is a state nobody chooses on purpose.

            Now the anchored folders are the list, and the target is a mark on one of them. The
            service sets it by itself when the first folder is anchored, so the ordinary case — one
            folder, everything goes there — needs no decision at all.
          */}
          {subject.folders.length > 0 && (
            <ul className="subject-detail__folders">
              {subject.folders.map((folder) => {
                const filing = subject.rootFolder?.id === folder.id
                return (
                  <li className="subject-detail__folder" key={folder.id} data-filing={filing}>
                    <span className="subject-detail__folder-path">{folder.path}</span>
                    {/*
                      A pressed control and never a native radio — see the rules in CLAUDE.md. It is
                      also un-pressable rather than a toggle: turning the target *off* would put the
                      subject back into "nowhere in particular", which is not a thing to choose.
                    */}
                    <button
                      type="button"
                      className="chip"
                      aria-pressed={filing}
                      disabled={filing}
                      onClick={() => {
                        onRootFolder(folder.id)
                      }}
                    >
                      {filing ? 'New things go here' : 'File new things here'}
                    </button>
                    <Button
                      tone="bare"
                      aria-label={`Stop anchoring ${folder.path}`}
                      onClick={() => {
                        onUnanchor(folder.id)
                      }}
                    >
                      Remove
                    </Button>
                  </li>
                )
              })}
            </ul>
          )}

          <FolderPicker
            label="Anchor a folder"
            folders={folders}
            takenIds={anchored}
            takenLabel="Anchored"
            onChoose={onAnchor}
          />
        </section>

        {/*
          What this subject is called in the systems content is taken over from.

          Under the folders and above what is in it, because it is the same kind of statement as an
          anchored folder — a rule that decides what belongs — and not a piece of the content itself.
          It draws nothing at all where nothing carries a code.
        */}
        <SourceKeys subjectId={subject.id} subjectName={subject.name} />

        {/*
          How work in this subject is marked. Beside the source keys and for the same sort of reason:
          it is a rule written once at the subject rather than a piece of its content — and a test set
          in this subject picks its standards from here.
        */}
        <MarkingSchemes subjectId={subject.id} />

        {/*
          The sessions of this subject, read back: what was on, what was written about it, and how
          much hangs on it. This is the review before an exam, and it is the reason a write-up exists
          at all — a term as a list of what came up and what is still to do, rather than a text to
          read again.
        */}
        {content !== null && content.appointments.length > 0 && (
          <SubjectReview
            appointments={content.appointments}
            onOpen={(id) => {
              onOpenContent('appointments', id)
            }}
          />
        )}

        {content !== null && <Held content={content} onOpen={onOpenContent} />}

        <section className="subject-detail__part">
          <h3 className="subject-detail__part-title">This subject</h3>
          <div className="controls">
            <Button
              tone="quiet"
              onClick={() => {
                onArchive(!isArchived(subject))
              }}
            >
              {isArchived(subject) ? 'Bring back' : 'Archive'}
            </Button>
            <Button
              tone="quiet"
              onClick={() => {
                setMerging((before) => !before)
              }}
            >
              Merge another into this
            </Button>
            {/*
              Deleting a subject is the one control here that asks first — not because anything is
              lost (no piece of content is touched) but because the belongings go, and rebuilding a
              term's worth of them by hand is exactly the work this area exists to save.
            */}
            {confirmRemove ? (
              <>
                <Button tone="quiet" className="control--danger" onClick={onRemove}>
                  Delete this subject
                </Button>
                <Button
                  tone="bare"
                  onClick={() => {
                    setConfirmRemove(false)
                  }}
                >
                  Keep it
                </Button>
              </>
            ) : (
              <Button
                tone="bare"
                onClick={() => {
                  setConfirmRemove(true)
                }}
              >
                Delete
              </Button>
            )}
          </div>
          {confirmRemove && (
            <p className="subject-detail__aside">
              Nothing that belongs to it is changed or deleted — only the belonging itself goes.
            </p>
          )}

          {merging && (
            <div className="subject-detail__choose">
              <label className="subject-detail__label" htmlFor="subject-merge">
                Merge which subject into this one?
              </label>
              <select
                id="subject-merge"
                value=""
                onChange={(event) => {
                  if (event.target.value !== '') {
                    onMerge(event.target.value)
                    setMerging(false)
                  }
                }}
              >
                <option value="">Choose a subject …</option>
                {all
                  .filter((one) => one.id !== subject.id)
                  .map((one) => (
                    <option value={one.id} key={one.id}>
                      {one.name}
                    </option>
                  ))}
              </select>
              <p className="subject-detail__aside">
                Everything that hangs on it moves here, and it goes. Something that hung on both
                hangs here exactly once.
              </p>
            </div>
          )}
        </section>
      </div>
    </article>
  )
}

/** Everything under the subject, grouped by kind, each group with a way into its own area. */
function Held({
  content,
  onOpen,
}: {
  readonly content: SubjectContent
  readonly onOpen: (kind: string, id: string) => void
}) {
  const groups = [
    {
      kind: 'documents',
      title: 'Documents',
      rows: content.documents.map((one) => ({
        id: one.document.id,
        label: one.document.title,
        inherited: one.inherited,
      })),
    },
    {
      kind: 'notes',
      title: 'Notes',
      rows: content.notes.map((one) => ({
        id: one.id,
        label: firstLine(one.text),
        inherited: false,
      })),
    },
    {
      kind: 'tasks',
      title: 'Tasks',
      rows: content.tasks.map((one) => ({ id: one.id, label: one.text, inherited: false })),
    },
    {
      kind: 'flashcards',
      title: 'Flashcards',
      rows: content.flashcards.map((one) => ({
        id: one.id,
        label: firstLine(one.front),
        inherited: false,
      })),
    },
    {
      kind: 'boards',
      title: 'Boards',
      rows: content.boards.map((one) => ({ id: one.id, label: one.name, inherited: false })),
    },
  ].filter((group) => group.rows.length > 0)

  if (groups.length === 0) {
    return (
      <section className="subject-detail__part">
        <h3 className="subject-detail__part-title">In this subject</h3>
        <p className="subject-detail__aside">
          Nothing yet. The quickest way to fill it is to anchor a folder above — everything in it
          belongs to this subject from that moment on.
        </p>
      </section>
    )
  }

  return (
    <section className="subject-detail__part">
      <h3 className="subject-detail__part-title">In this subject</h3>
      {groups.map((group) => (
        <div className="subject-detail__group" key={group.kind}>
          <h4 className="subject-detail__group-title">
            {group.title}
            <span className="subject-detail__count">{group.rows.length}</span>
          </h4>
          <ul className="subject-detail__held">
            {group.rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className="subject-detail__held-open"
                  onClick={() => {
                    onOpen(group.kind, row.id)
                  }}
                >
                  <span>{row.label === '' ? 'Untitled' : row.label}</span>
                  {row.inherited && <span className="subject-detail__from">from a folder</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}

function firstLine(text: string): string {
  const line = text.split('\n').find((one) => one.trim() !== '')
  return (line ?? '').replace(/^#+\s*/, '').trim()
}
